import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Control } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetClientList } from "../../api/services/client/clientApis";
import { GetClientListResponse } from "../../api/services/client/models";
import {
  ClientTypeEnum,
  DEBOUNCE_THRESHOLD_CLIENT,
  TIMEOUT_300,
} from "../../helpers/constants";
import { getValueOrBlank, isNotEmpty } from "../../helpers/utils";
import { useDebounce } from "../../hooks/useDebounce";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import {
  AutoCompleteOption,
  MuiAutoComplete,
} from "../autoComplete/MuiAutoComplete";
import { FormAutoCompleteController } from "../formController/FormAutoCompleteController";

interface ClientAutoCompleteProps {
  label: string;
  formId: string;
  control: Control<any>;
  disabled?: boolean;
  onChangeValue?: (value: AutoCompleteOption) => void;
  isMandatory?: boolean;
  showCloseIcon?: boolean;
  isActiveClientOnly?: boolean;
  selectedValue?: AutoCompleteOption | null;
  excludeNCBPClient?: boolean;
  clientType?: ClientTypeEnum;
  infoMessage?: string;
  updateValueAlways?: boolean;
}
interface ClientAutoCompleteRef {
  resetValues: () => void;
}

export const ClientAutoComplete = forwardRef<
  ClientAutoCompleteRef,
  ClientAutoCompleteProps
>((props, ref) => {
  const [initialized, setInitialized] = useState(false);
  const [clientInputValue, setClientInputValue] = useState("");
  const debouncedValue = useDebounce(clientInputValue, TIMEOUT_300);
  const [clientListOption, setClientListOption] = useState<
    AutoCompleteOption[]
  >([]);
  const { commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    data: clientList,
    isFetching: isClientListLoading,
    refetchWithParams: refetchClientList,
  } = useGetClientList();
  const dispatch = useDispatch();

  const {
    label,
    formId,
    disabled,
    control,
    onChangeValue,
    isMandatory,
    showCloseIcon,
    isActiveClientOnly = false,
    excludeNCBPClient = false,
    clientType,
    selectedValue,
    infoMessage,
    updateValueAlways = false,
  } = props;

  const resetValues = () => {
    setClientInputValue("");
    setClientListOption([]);
  };

  useEffect(() => {
    if (isNotEmpty(selectedValue) && (updateValueAlways || !initialized)) {
      setClientListOption([selectedValue]);
      setClientInputValue(getValueOrBlank(selectedValue.label));

      if (!updateValueAlways) {
        setInitialized(true);
      }
    } else if (updateValueAlways) {
      setClientInputValue("");
      setClientListOption([]);
    }
  }, [selectedValue, updateValueAlways, initialized]);

  useImperativeHandle(ref, () => ({
    resetValues,
  }));

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isClientListLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isClientListLoading]);

  useEffect(() => {
    if (debouncedValue.length >= DEBOUNCE_THRESHOLD_CLIENT) {
      refetchClientList({
        keyword: debouncedValue,
        isActiveClientOnly,
        excludeNCBPClient,
        clientType: clientType,
      });
    }
    setClientListOption([]);
  }, [debouncedValue, isActiveClientOnly, excludeNCBPClient, clientType]);

  useEffect(() => {
    if (clientList && isNotEmpty(clientList)) {
      const options: AutoCompleteOption[] = clientList.map(
        (client: GetClientListResponse) => ({
          id: client.id,
          label: client.name,
          labelTwo: client.clientCode,
          clientType: client.clientType,
        }),
      );
      setClientListOption(options);
    } else {
      setClientListOption([]);
    }
  }, [clientList]);

  return (
    <FormAutoCompleteController control={control} name={formId}>
      <MuiAutoComplete
        label={label}
        placeholder={commonLabels.typeToSearchPlaceholder2Chars}
        requiredARIALabel={commonARIALabel.required}
        isMandatory={isMandatory}
        options={clientListOption}
        showCloseIcon={showCloseIcon}
        inputValue={clientInputValue}
        setInputValue={setClientInputValue}
        isInputValueControlled
        id={formId}
        disabled={disabled}
        noOptionsText={commonLabels.noResult}
        onChange={(_event, option) => {
          if (option && onChangeValue) {
            onChangeValue(option);
          }
        }}
        infoMessage={infoMessage}
      />
    </FormAutoCompleteController>
  );
});
